import React, { useEffect, useState } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { FEATURE_ACCESS, PRICING_PLANS, FEATURES } from '../../config/Config';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps, createStatusChip, statusConfigs } from '../../utils/tableUtils';

export default function FeatureAccessManagementPage() {
  const [access, setAccess] = useState([]);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ pricing_plan_name: '', feature_label: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pricing_plan: '', feature: '', is_enabled: true });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPaginated = async (endpoint) => {
    let url = endpoint;
    let all = [];
    while (url) {
      const res = await api.get(url);
      const data = res.data;
      if (Array.isArray(data)) {
        all = data; // non-paginated
        break;
      }
      all = all.concat(data.results || []);
      url = data.next || null;
    }
    return all;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [allAccess, allPlans, allFeatures] = await Promise.all([
        fetchPaginated(FEATURE_ACCESS),
        fetchPaginated(PRICING_PLANS),
        fetchPaginated(FEATURES)
      ]);
      setAccess(allAccess);
      setPlans(allPlans);
      setFeatures(allFeatures);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filteredAccess = filterData(access, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { pricing_plan_name: '', feature_label: '' });

  const filterConfig = [
    {
      field: 'pricing_plan_name',
      label: 'Pricing Plan',
      type: 'select',
      options: plans.map(plan => ({ value: plan.name, label: plan.name }))
    },
    {
      field: 'feature_label',
      label: 'Feature',
      type: 'text',
      placeholder: 'Search by feature...'
    }
  ];

  const handleOpen = (item = null) => {
    if (item) {
      setForm({ pricing_plan: item.pricing_plan, feature: item.feature, is_enabled: item.is_enabled });
      setEditMode(true);
      setCurrentId(item.id);
    } else {
      setForm({ pricing_plan: '', feature: '', is_enabled: true });
      setEditMode(false);
      setCurrentId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ pricing_plan: '', feature: '', is_enabled: true });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = async () => {
    if (!form.pricing_plan || !form.feature) {
      showSnackbar('Please select both plan and feature', 'error');
      return;
    }

    try {
      if (editMode) {
        await api.patch(`${FEATURE_ACCESS}${currentId}/`, form);
        showSnackbar('Feature access updated successfully');
      } else {
        await api.post(FEATURE_ACCESS, form);
        showSnackbar('Feature access created successfully');
      }
      handleClose();
      fetchAll();
    } catch (err) {
      console.error('[FeatureAccess] Save failed', err?.response?.data || err);
      const data = err?.response?.data || {};
      let message = 'Operation failed';
      if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
        message = data.non_field_errors.join(' ');
      } else if (typeof data.detail === 'string') {
        message = data.detail;
      } else if (typeof data.error === 'string') {
        message = data.error;
      }
      showSnackbar(message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feature access?')) return;
    try {
      await api.delete(`${FEATURE_ACCESS}${id}/`);
      showSnackbar('Feature access deleted successfully');
      fetchAll();
    } catch (err) {
      console.error(err);
      showSnackbar('Delete failed', 'error');
    }
  };

  const columns = [
    {
      field: 'pricing_plan_name',
      headerName: 'Pricing Plan',
      flex: 2,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'feature_label',
      headerName: 'Feature',
      flex: 2
    },
    {
      field: 'is_enabled',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const status = params.value ? 'active' : 'inactive';
        const chipProps = createStatusChip(status, statusConfigs || {});
        return <Chip {...chipProps} />;
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add Feature Access',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No feature access found',
    description: 'Try adjusting your filters or add your first feature access.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Feature Access Management"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredAccess.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <DataGrid
            key={`${filters.pricing_plan_name || 'all'}-${filters.feature_label || 'all'}`}
            autoHeight
            rows={filteredAccess}
            columns={columns}
            getRowId={(row) => row.id}
            {...dataGridProps}
            components={{
              Toolbar: () => (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2,
                  p: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GridToolbarColumnsButton 
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                    <GridToolbarDensitySelector 
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                    <GridToolbarExport 
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.50' }
                      }}
                    />
                  </Box>
                </Box>
              )
            }}
          />
        </Box>
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Feature Access' : 'Add Feature Access'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Pricing Plan</InputLabel>
            <Select
              value={form.pricing_plan}
              label="Pricing Plan"
              onChange={(e) => setForm({ ...form, pricing_plan: e.target.value })}
            >
              {plans.map(plan => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Feature</InputLabel>
            <Select
              value={form.feature}
              label="Feature"
              onChange={(e) => setForm({ ...form, feature: e.target.value })}
              disabled={features.length === 0}
            >
              {features.length === 0 ? (
                <MenuItem disabled>
                  {loading ? 'Loading features...' : 'No features available'}
                </MenuItem>
              ) : (
                features.map(feature => (
                  <MenuItem key={feature.id} value={feature.id}>
                    {feature.name || feature.label || feature.feature_name || feature.id}
                  </MenuItem>
                ))
              )}
            </Select>
            {features.length === 0 && !loading && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                No features loaded. Check console for debugging info.
              </Typography>
            )}
          </FormControl>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              Enable Feature
            </Typography>
            <Switch
              checked={form.is_enabled}
              onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
